// src/components/institution/manager/EventsCalendarTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Eye, 
  Filter, 
  Star, 
  AtSign, 
  Tag,
  ChevronLeft,
  ChevronRight,
  Grid,
  List as ListIcon,
  Download
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';
import { EventForm } from '../forms/EventForm';

export const EventsCalendarTab = ({ bookId, institutionData, onUpdate }) => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'calendar'
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    loadEvents();
  }, [bookId]);

  useEffect(() => {
    applyFilters();
  }, [events, searchTerm, selectedCategory, selectedStatus]);

  const loadEvents = async () => {
    try {
      const eventsData = await institutionService.getEvents(bookId);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date formatting helpers
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toDate = (yyyy_mm_dd, hh_mm) => {
    if (!yyyy_mm_dd) return null;
    return new Date(`${yyyy_mm_dd}T${hh_mm || '00:00'}:00`);
  };

  const getEventStatus = (startDate, endDate, startTime, endTime) => {
    const now = new Date();
    const start = toDate(startDate, startTime) || (startDate ? new Date(startDate) : null);
    const end = toDate(endDate, endTime) || 
                (endDate ? new Date(`${endDate}T23:59:59`) : 
                (start ? new Date(start.getTime() + 86400000) : null));

    if (!start) return { status: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    if (now < start) return { status: 'Upcoming', color: 'bg-blue-100 text-blue-800' };
    if (end && now > end) return { status: 'Past', color: 'bg-gray-100 text-gray-600' };
    return { status: 'Ongoing', color: 'bg-green-100 text-green-800' };
  };

  const applyFilters = () => {
    let filtered = [...events];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(event => {
        const status = getEventStatus(event.startDate, event.endDate, event.startTime, event.endTime);
        return status.status.toLowerCase() === selectedStatus;
      });
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB;
    });

    setFilteredEvents(filtered);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      if (editingEvent) {
        await institutionService.updateEvent(bookId, editingEvent.id, eventData);
      } else {
        await institutionService.createEvent(bookId, eventData);
      }
      
      await loadEvents();
      await onUpdate?.();
      setShowForm(false);
      setEditingEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await institutionService.deleteEvent(bookId, eventId);
        await loadEvents();
        await onUpdate?.();
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete event');
      }
    }
  };

  // Calendar view helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const getEventsForDay = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return filteredEvents.filter(event => {
      if (!event.startDate) return false;
      const eventDate = event.startDate.split('T')[0];
      return eventDate === dateStr;
    });
  };

  const changeMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  const categories = [...new Set(events.map((e) => e.category).filter(Boolean))];

  // Render Calendar View
  const renderCalendarView = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const monthName = currentDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    
    const weeks = [];
    let currentWeek = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      currentWeek.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    // Add remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">{monthName}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="space-y-2">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-2">
              {week.map((day, dayIndex) => {
                const dayEvents = day ? getEventsForDay(day) : [];
                const isToday = day && 
                  day === new Date().getDate() && 
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();

                return (
                  <div
                    key={dayIndex}
                    className={`min-h-[100px] p-2 rounded-lg border transition-colors ${
                      day 
                        ? isToday
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'bg-white border-gray-200 hover:border-indigo-200'
                        : 'bg-gray-50 border-transparent'
                    }`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-semibold mb-1 ${
                          isToday ? 'text-indigo-600' : 'text-gray-700'
                        }`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 2).map(event => (
                            <div
                              key={event.id}
                              onClick={() => {
                                setEditingEvent(event);
                                setShowForm(true);
                              }}
                              className="text-xs p-1 rounded bg-indigo-100 text-indigo-700 truncate cursor-pointer hover:bg-indigo-200 transition-colors"
                              title={event.title}
                            >
                              {event.startTime && (
                                <span className="font-medium">{formatTime(event.startTime)} </span>
                              )}
                              {event.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-xs text-gray-500 pl-1">
                              +{dayEvents.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Event Card (improved design)
  const renderEventCard = (event) => {
    const {
      id,
      title,
      description,
      category,
      location,
      startDate,
      endDate,
      startTime,
      endTime,
      capacity,
      isPublic,
      isFeatured,
      organizer,
      contactEmail,
      tags,
      eventImage
    } = event;

    const eventStatus = getEventStatus(startDate, endDate, startTime, endTime);
    const cap = Number.isFinite(parseInt(capacity, 10)) ? parseInt(capacity, 10) : null;

    return (
      <div key={id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
        {/* Event Image */}
        {eventImage ? (
          <div className="relative h-48 bg-gray-100 overflow-hidden">
            <img
              src={typeof eventImage === 'string' ? eventImage : eventImage.url}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-3 right-3 flex gap-2">
              {isFeatured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white shadow-lg">
                  <Star className="h-3.5 w-3.5" /> Featured
                </span>
              )}
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium shadow-lg ${eventStatus.color}`}>
                {eventStatus.status}
              </span>
            </div>
          </div>
        ) : (
          <div className="relative h-48 bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
            <Calendar className="h-16 w-16 text-indigo-300" />
            <div className="absolute top-3 right-3 flex gap-2">
              {isFeatured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white">
                  <Star className="h-3.5 w-3.5" /> Featured
                </span>
              )}
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${eventStatus.color}`}>
                {eventStatus.status}
              </span>
            </div>
          </div>
        )}

        {/* Event Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                {title}
              </h3>
              <div className="flex flex-wrap gap-2">
                {category && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {category}
                  </span>
                )}
                {isPublic && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                    <Eye className="h-3 w-3" /> Public
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingEvent(event);
                  setShowForm(true);
                }}
                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteEvent(id);
                }}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {description && (
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {description}
            </p>
          )}

          <div className="space-y-2">
            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>
                {formatDate(startDate)}
                {endDate && startDate !== endDate && ` – ${formatDate(endDate)}`}
              </span>
            </div>

            {/* Time */}
            {(startTime || endTime) && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span>
                  {startTime && formatTime(startTime)}
                  {endTime && ` – ${formatTime(endTime)}`}
                </span>
              </div>
            )}

            {/* Location */}
            {location && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{location}</span>
              </div>
            )}

            {/* Capacity */}
            {cap !== null && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span>Capacity: {cap}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          {(organizer || contactEmail || (tags && tags.length > 0)) && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
              {organizer && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <AtSign className="h-3.5 w-3.5" />
                  <span>Organized by {organizer}</span>
                </div>
              )}
              
              {tags && tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                  {tags.length > 3 && (
                    <span className="text-xs text-gray-500 px-2 py-0.5">
                      +{tags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Calendar className="h-6 w-6 text-indigo-600" />
            Events & Calendar
          </h2>
          <p className="text-gray-600">Manage institutional events and academic calendar</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Event
          </button>
          
          <button
            onClick={() => institutionService.exportEvents(bookId)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Events</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="past">Past</option>
            </select>

            {/* View Mode Toggles */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="List View"
              >
                <ListIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'calendar' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Calendar View"
              >
                <Calendar className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{events.length}</p>
            </div>
            <Calendar className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">
                {events.filter(e => getEventStatus(e.startDate, e.endDate, e.startTime, e.endTime).status === 'Upcoming').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ongoing</p>
              <p className="text-2xl font-bold text-green-600">
                {events.filter(e => getEventStatus(e.startDate, e.endDate, e.startTime, e.endTime).status === 'Ongoing').length}
              </p>
            </div>
            <Star className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Public Events</p>
              <p className="text-2xl font-bold text-purple-600">
                {events.filter(e => e.isPublic).length}
              </p>
            </div>
            <Eye className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Events Display */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events Found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first event'}
          </p>
          {!searchTerm && selectedCategory === 'all' && selectedStatus === 'all' && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              Add Event
            </button>
          )}
        </div>
      ) : viewMode === 'calendar' ? (
        renderCalendarView()
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map(event => renderEventCard(event))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-4">
          {filteredEvents.map(event => renderEventCard(event))}
        </div>
      )}

      {/* Event Form Modal */}
      {showForm && (
        <EventForm
          bookId={bookId}
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={() => {
            setShowForm(false);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
};